import React from 'react';
import { UserApi } from 'api';
import { useAuthContext } from 'hooks/useAuthContext';
import { registrationUrl } from '@gr-monorepo/common/public/ui/constants';
import { FormEvent, useState } from 'react';
import { useClickStreamContext } from '../ClickStream';
import {
  Fields,
  FormWrapper,
  HelpWrapper,
  LoginTitle,
  SubmitWrapper,
  Link,
  AccessRequest,
  ErrorMessage,
  ErrorIcon,
} from './elements';
import { FieldGroup, FieldLabel, Input } from '@gr-monorepo/common/public/ui';
import { Space } from '@gr-monorepo/common/ui';
import LoginLoader from '../LoginLoader';
import { PrimaryButton } from '@gr-monorepo/common/public/ui/Buttons';

export const MINISTER_USER_TYPE = 'ministr';

const useLoginForm = () => {
  const [formData, setFormData] = useState<{ login: string; password: string; fieldError: string; authError: string }>({
    login: '',
    password: '',
    fieldError: '',
    authError: '',
  });
  const [pending, setPending] = useState(false);
  const { fetchUserData } = useAuthContext();

  const submitForm = async () => {
    setPending(true);
    const data = {
      login: formData.login,
      password: formData.password,
      user_type: MINISTER_USER_TYPE,
    };

    try {
      await UserApi.login({ data });
      setPending(true);
      localStorage.setItem('UPR_logged', 'true');
      fetchUserData();
      setFormData({ ...formData, fieldError: '', authError: '' });
    } catch (err: any) {
      console.log(err.status === 401);
      setFormData({
        ...formData,
        fieldError: '',
        authError: err.status === 401 ? 'Неверный логин или пароль' : 'Ошибка входа',
      });
      setPending(false);
    }
  };
  return { formData, setFormData, submitForm, pending };
};
const LoginForm = () => {
  const { formData, setFormData, submitForm, pending } = useLoginForm();
  const { logPageEvent } = useClickStreamContext();
  const hasFieldError = !!formData.fieldError;
  const hasError = hasFieldError || !!formData.authError;

  const handleLogin = (value: string) => {
    setFormData({ ...formData, fieldError: '', authError: '', login: value });
  };
  const handlePassword = (value: string) => {
    setFormData({ ...formData, fieldError: '', authError: '', password: value });
  };
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    logPageEvent({
      eventType: 'login_submit',
      eventInfo: `Клик по кнопке авторизации в форме логина`,
      eventSource: 'login_municipality',
    });
    if (!formData.login || !formData.password) {
      setFormData({ ...formData, authError: '', fieldError: 'Укажите логин и пароль' });
      return;
    }
    submitForm();
  };

  return (
    <FormWrapper onSubmit={handleSubmit} data-testid="login-form">
      <LoginTitle>
        Вход в кабинет региона
        <br />и муниципалитета
      </LoginTitle>
      <Fields>
        <FieldGroup>
          <FieldLabel>Логин</FieldLabel>
          <Input
            inputProps={{ type: 'text' }}
            value={formData.login}
            onChange={handleLogin}
            error={hasError}
            dataQaId="input_login"
            data-testid="login-form-username"
          />
        </FieldGroup>
        <Space size={20} axis="vertical" style={{ position: 'relative', width: '100%' }}>
          {pending && (
            <LoginLoader style={{ position: 'absolute', left: '50%', transform: 'translate(-50%)', top: 11 }} />
          )}
        </Space>

        <FieldGroup>
          <FieldLabel>Пароль</FieldLabel>
          <Input
            inputProps={{ type: 'password' }}
            value={formData.password}
            onChange={handlePassword}
            error={hasError}
            dataQaId="input_password"
            data-testid="login-form-password"
          />
        </FieldGroup>
        {formData.fieldError && (
          <>
            <Space size={20} axis="vertical" />
            <ErrorMessage>
              <ErrorIcon />
              {formData.fieldError}
            </ErrorMessage>
          </>
        )}
      </Fields>
      <SubmitWrapper>
        <PrimaryButton
          type="submit"
          size="xlarge"
          fullWidth
          disabled={pending}
          data-testid="login-form-submit"
        >
          Войти
        </PrimaryButton>
      </SubmitWrapper>
      {formData.authError && (
        <>
          <Space size={20} axis="vertical" />
          <ErrorMessage data-testid={'invalid_creds'}>
            <ErrorIcon />
            {formData.authError}
          </ErrorMessage>
        </>
      )}
      <Space size={16} axis="vertical" />
      <AccessRequest
        onClick={() => {
          logPageEvent({
            eventType: 'click',
            eventInfo: 'login_click_registration',
            eventSource: 'login_municipality',
          });
        }}
        href={registrationUrl}
        data-qa-id="button_registration"
      >
        Подать заявку на регистрацию
      </AccessRequest>
      <Space size={20} axis="vertical" />
      <HelpWrapper>
        Если возникли трудности, напишите нам:{' '}
        <Link
          onClick={() => {
            logPageEvent({
              eventType: 'click_mailto_support',
              eventInfo: `Клик по ссылке электронной почты саппорта`,
              eventSource: 'login_municipality',
            });
          }}
          href="mailto:support_kabmun@uchi.ru"
          data-qa-id="support_mail"
        >
          support_kabmun@uchi.ru
        </Link>
      </HelpWrapper>
      <Space size={16} axis="vertical" />
      <HelpWrapper>
        Личный кабинет ученика и родителя: <br />
        <Link
          onClick={() => {
            logPageEvent({
              eventType: 'click_go_to_uchi',
              eventInfo: `Клик по ссылке главная учиру`,
              eventSource: 'login_municipality',
            });
          }}
          href="https://uchi.ru/"
          data-qa-id="uchiru_link"
        >
          https://uchi.ru
        </Link>
      </HelpWrapper>
    </FormWrapper>
  );
};

export default LoginForm;
